import React, { useContext, useEffect, useRef, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';

import { makeStyles, fade } from '@material-ui/core/styles';

import SpeedDial from '@material-ui/lab/SpeedDial';
import MoreHorizIcon from '@material-ui/icons/MoreHoriz';
import SpeedDialAction from '@material-ui/lab/SpeedDialAction';
import { Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle } from '@material-ui/core';

import api from '../../utils/api';

import { WarningButton } from '../CustomMui';
import { clearGlobalErrors } from '../../actions';
import LockContext from '../../contexts/LockContext';

const root = process.env.REACT_APP_API_ROOT || '';

const useStyles = makeStyles((theme) => ({
  radioGroup: {
    margin: theme.spacing(1, 0),
  },
  speedDial: {
    position: 'absolute',
    right: 20,
    '&.MuiSpeedDial-directionDown': {
      top: theme.spacing(2),
    },
  },
  speedDialAction: {
    boxShadow: `0px 1px 6px ${fade('#000', .16)}`,
    color: theme.palette.primary.main,
    fontSize: 18,
    '&:hover': {
      color: theme.palette.primary.hover,
      backgroundColor: theme.palette.primary.light,
    }
  },
  disabled: { 
    ...theme.overrides.MuiFab.root['&$disabled'],
    '&:hover': {
      ...theme.overrides.MuiFab.root['&$disabled'],
    },
  }
}));

const defaultCopyLinkText = 'Copy preview link';
const copiedLinkText = 'Copied link!';

export const InteractiveSpeedDial = ({setNotification, ...props}) => {
  const classes = useStyles();
  const history = useHistory();
  const { id } = useParams();
  const dispatch = useDispatch();
  const dirty = useSelector(state => state.dirty);
  const globalErrors = useSelector(state => state.errors);
  const { lock } = useContext(LockContext);

  const [open, setOpen] = useState(false);
  const [dialog, setDialog] = useState({});
  const [deleting, setDeleting] = useState(false);
  const [copyLinkText, setCopyLinkText] = useState(defaultCopyLinkText)

  const speedDialRef = useRef(null);

  // function(event: object, reason: string) => void
  // event: The event source of the callback.
  // reason: Can be: "toggle", "blur", "mouseLeave", "escapeKeyDown".
  const handleClose = (e,reason) => {
    if (reason === 'blur' && e && e.target) {
      const domTarget = document.getElementsByClassName(e.target.className).item(0);

      if (speedDialRef.current.contains(domTarget)) {
        setOpen(true);
        return;
      }
    }
    
    setCopyLinkText(defaultCopyLinkText)
    setOpen(false);      
  
  };


  const handleOpen = () => {
    setOpen(true);
  };

  // Set dialog text and confirm action for delete
  const handleDeleteDialogOpen = () => {
    setDialog({
      open: true,
      title: 'Are you sure you want to delete this item?',
      actionButton: <WarningButton onClick={deleteItem} autoFocus disabled={deleting}>
                      Delete
                    </WarningButton>
    });
  };

  // Determine & set dialog text and actions for export
  useEffect(() => {

    if (dialog.action !== 'export') return;

    let newDialog = {
      action: 'export',
      open: true,
      title: "Are you ready to export files?",
      text: "Export item’s files to upload to the PAVILION site. Remember to review your content before publishing on PAVILION.",
      actionButton: <Button 
                      component={'a'} 
                      href={`${root}/api/interactives/${id}/export/`} 
                      download 
                      onClick={handleDialogClose} 
                      color="primary" 
                      autoFocus
                    >
                      Export
                    </Button>
    }

    // Not the best way to manage errors; would be better to point out where exactly they are.
    // But validation was a last-minute task and any was better than none.
    if (Object.keys(globalErrors).length > 0) {
      let text = `${Object.keys(globalErrors).length} errors have been flagged in red. It is recommended to correct these errors prior to exporting files and uploading to the PAVILION site.`;

      if (Object.keys(globalErrors).length === 1) {
        text =  `1 error has been flagged in red. It is recommended to correct this error prior to exporting files and uploading to the PAVILION site.`;
      }

      newDialog = {
        action: 'export',
        open: true,
        title: "Your export files contain errors.",
        text: text,
        actionButton: <WarningButton 
                        component={'a'} 
                        href={`${root}/api/interactives/${id}/export/`} 
                        download 
                        onClick={handleDialogClose} 
                        autoFocus
                      >
                        Export with errors
                      </WarningButton>
      }
    } 
    
    if (dialog.text !== newDialog.text ) {    
      setDialog(newDialog);
    }
  }, [dialog,globalErrors,id])

  const handleExportDialogOpen = () => {
    if (dirty) {
      return;
    } 
    
    dispatch(clearGlobalErrors());
    window.dispatchEvent(new Event('validate'))

    setDialog({
      action: 'export',
      open: true,
    });
  };

  const handleDialogClose = () => {
    setDialog({});
  };

  // Force a refresh of the Preview
  const refreshFrame = () => {
    window.dispatchEvent(new Event('refreshFrame'))
    handleClose();
  }

  const copyLink = () => {
    const link = document.createElement('textarea');
    link.value = `${window.location.origin}/preview/${id}`;
    document.body.appendChild(link);
    link.select();
    document.execCommand('copy');
    document.body.removeChild(link);

    // alert user
    setCopyLinkText(copiedLinkText)
   
  }

  const deleteItem = () => { 
    if (id) {
      setDeleting(true)
      api.deleteInteractive(id, lock.id)
        .then(res => {
          history.push('/', {dirty:false});
        })
        .catch(err => {
          let message = 'An error occurred. This item was not deleted.';

          switch (err.response.status) {
            case 423:
              message = 'Can\'t delete. This item is locked for edits.';
              break;
            case 500:
              message = 'An internal server error occurred. This item was not deleted.';
              break;
            default:
              break;
          }

          setDialog({});

          setNotification({
            open: true,
            message: message,
            severity: 'error',
          })
          setDeleting(false)
          console.log(err)
        })
    }
    // If no id, this intv has never beed saved. Refresh to discard.
    else {
      window.location.reload();
    }
  }

  // Buttons in the SpeedDial
  const actions = [
    { 
      name: 'Refresh preview', 
      icon: <i className="fas fa-sync-alt"></i>, 
      onClick: refreshFrame 
    },
    { 
      name: 'Export files', 
      icon: <i className="fas fa-file-export"></i>, 
      onClick: handleExportDialogOpen, // open dialog; dialog confirm has actual export
      disabled: dirty || !id, 
      disabledMessage: "Save before exporting",
    },
    { 
      name: copyLinkText, 
      icon: <i className="fas fa-link"></i>, 
      onClick: copyLink,  
      disabled: !id, 
      disabledMessage: "Save to get a preview link",
    },
    { 
      name: 'Delete item', 
      icon: <i className="far fa-trash-alt"></i>, 
      onClick: handleDeleteDialogOpen, // open dialog; dialog confirm has actual delete
      disabled: !!lock.owner && !lock.isCurrent,
      disabledMessage: "Can’t delete. This item is locked for edits.",
    },
    // { 
    //   name: 'Check for errors', 
    //   icon: <i className="fas fa-tasks"></i>, 
    //   onClick: () => {
    //     dispatch(clearGlobalErrors());
    //     window.dispatchEvent(new Event('validate'))
    //     handleClose();
    //   } 
    // },
  ];

  return ( 
    <div ref={speedDialRef}>
      <SpeedDial
        ariaLabel="Additional Controls"
        className={classes.speedDial}
        icon={<MoreHorizIcon />}
        onClose={handleClose}
        onOpen={handleOpen}
        open={open}
        direction="down"
      >
        {actions.map((action) => (
          <SpeedDialAction
            key={action.name}
            icon={action.icon}
            tooltipTitle={action.disabled ? action.disabledMessage : action.name}
            onClick={action.onClick ? action.onClick : handleClose}
            className={`${classes.speedDialAction} ${action.disabled ? classes.disabled : ''}`}
            FabProps={action.disabled ? {disableRipple:true} : action.fabProps ? action.fabProps : {}}
          />
        ))}
      </SpeedDial>


      {/* Dialog - used for prompt both when exporting and deleting  */}
      <Dialog
        open={!!dialog.open}
        onClose={handleDialogClose}
        aria-labelledby="sd-dialog-title"
        aria-describedby="sd-dialog-description"
      >

        <DialogTitle id="sd-dialog-title">{dialog.title}</DialogTitle>

        {dialog.text ?
          <DialogContent>
            <DialogContentText id="sd-dialog-description" component="div">
              {dialog.text}
            </DialogContentText>
          </DialogContent>
        : '' }

        <DialogActions>
          <Button onClick={handleDialogClose} variant="text">
            Cancel
          </Button>

          {dialog.actionButton}
          
        </DialogActions>

      </Dialog>

    </div>

  );
}